import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { RefreshCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  ProviderTable,
  DailySpendChart,
  BudgetCard,
  TopWorkspacesCard,
  TopConsumersCard,
} from "./usage/AiUsageCards";

type DailyPoint = { date: string; cost_usd: number };

type UsageStats = {
  generated_at: string;
  window_start: string;
  month_start: string;
  cost_by_provider_30d: Record<string, number>;
  tokens_in_by_provider_30d: Record<string, number>;
  tokens_out_by_provider_30d: Record<string, number>;
  events_by_outcome_30d: Record<string, number>;
  failover_count_30d: number;
  total_events_30d: number;
  admin_spend_this_month_total_usd: number;
  monthly_admin_budget_total_usd: number;
  projected_month_end_admin_spend_usd: number;
  daily_admin_spend_30d: DailyPoint[];
  top_admin_funded_workspaces: Array<{
    workspace_id: string;
    workspace_label: string | null;
    account_tier: string | null;
    admin_spend_this_month_usd: number;
  }>;
  top_credit_consumers: Array<{
    owner_user_id: string;
    display_name: string;
    account_tier: string;
    workspace_count: number;
    credits_used_this_month: number;
    topup_balance: number;
  }>;
};

const fetchStats = async (): Promise<UsageStats> => {
  const { data, error } = await supabase.functions.invoke("admin-ai-usage-stats", { body: {} });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data as UsageStats;
};

const fmtRelative = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs} hr ago`;
};

export const AiUsageTab = () => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-ai-usage-stats"], queryFn: fetchStats });

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (q.isError) {
    return (
      <Card className="shadow-soft border-destructive/30">
        <CardContent className="py-4 text-sm text-destructive">
          Couldn't load AI usage stats: {(q.error as Error).message}
        </CardContent>
      </Card>
    );
  }

  const data = q.data!;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Generated {fmtRelative(data.generated_at)} · Window:{" "}
          {new Date(data.window_start).toLocaleDateString()} → today
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => qc.invalidateQueries({ queryKey: ["admin-ai-usage-stats"] })}
          disabled={q.isFetching}
        >
          <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${q.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <ProviderTable data={data} />
      <DailySpendChart data={data} />
      <BudgetCard data={data} />
      <TopWorkspacesCard data={data} />
      <TopConsumersCard data={data} />
    </div>
  );
};
